import { useState, useEffect, Fragment } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import {
  composerImageUrlsSelector,
  composerSelectedProfilesSelector,
  composerVideoUrlSelector,
  composerPostSelector
} from 'services/compose/selectors'
import { InstagramLocationSelector } from './InstagramLocationSelector'
import styles from './CustomizePostBlock.pcss'
import { setInstagramPostType, setInstagramPostLocation } from 'services/compose'
import Button from '@mui/material/Button'
import IconLocation from '@mui/icons-material/Place'
import { PPDialog } from 'components/PPDialog'
import PPSwitch from 'components/PPSwitch'
import StoreState from 'store/state'
import { LocationInfo, ComposerInstagramPost, IG_POST_TYPE_REEL, POST_TYPE_STORY, BRAND_INSTAGRAM } from 'shared'

// Keeps the picker below the composer chrome, and below the location dropdown popper (z-index 212).
const DIALOG_Z_INDEX = 211

interface ComposerInstagramPostOptionsProps {
  className?: string
  hidePostTypeSelector?: boolean
  onSelectedLocationChange?: (location: LocationInfo | undefined, ppid: string) => void
}

export function ComposerInstagramPostOptions(props: ComposerInstagramPostOptionsProps) {
  const dispatch = useDispatch()
  const [popupOpen, setPopupOpen] = useState(false)

  // const rememberLocation = useSelector(rememberIGLocationsSelector)
  const post = useSelector((state: StoreState) => composerPostSelector(state, BRAND_INSTAGRAM)) as ComposerInstagramPost
  const videoUrl = useSelector(composerVideoUrlSelector)
  const images = useSelector(composerImageUrlsSelector)
  const profiles = useSelector(composerSelectedProfilesSelector).filter(p => p.type === BRAND_INSTAGRAM)
  const locationEnabledProfiles = profiles.filter(p => !p.instagramDirect)

  // Only vids can be posted as reel. Reset post type if user adds images to a reel post
  useEffect(() => {
    if (post.postType === IG_POST_TYPE_REEL && images.length > 0) {
      dispatch(setInstagramPostType(undefined))
    }
  }, [images.length, post.postType, dispatch])

  const updateSelectedLocation = (location: LocationInfo | undefined, ppid: string) => {
    if (props.onSelectedLocationChange) {
      props.onSelectedLocationChange(location, ppid)
    } else {
      dispatch(setInstagramPostLocation({ location, ppid }))
    }
  }

  const onPostTypeChange = (postType: string) => {
    const selectedPostType = postType === 'post' ? undefined : postType
    dispatch(setInstagramPostType(selectedPostType))
  }

  const openPopup = () => {
    setPopupOpen(true)
  }

  const closePopup = () => {
    setPopupOpen(false)
  }

  // const toggleRememberLocation = () => {
  //   dispatch(toggleRememberIGLocation())
  // }

  const multipleLocationsSelected = profiles.length > 1
    && Object.keys(post.locations).filter(id => Boolean(post.locations[id])).length > 0

  const postTypeOptions = [{
    value: 'post',
    label: videoUrl ? 'Post/Reel' : 'Post'
  }, {
    value: POST_TYPE_STORY,
    label: 'Story'
  }]

  return (
    <div className={props.className} data-testid="composer-instagram-post-options">
      {!props.hidePostTypeSelector && (
        <div className={styles['ig-switch-group']}>
          <PPSwitch
            selectedValue={post.postType || 'post'}
            options={postTypeOptions}
            onSelectedValueChange={onPostTypeChange}
          />
        </div>
      )}
      {post.postType !== POST_TYPE_STORY && (
        <div className={styles['option-box']}>
          {locationEnabledProfiles.length === 1 && (
            <InstagramLocationSelector
              profile={locationEnabledProfiles[0]}
              useGenericImage
              selectedLocation={post.locations[locationEnabledProfiles[0].ppid]}
              onSelectedLocationChange={updateSelectedLocation}
            />
          )}
          {locationEnabledProfiles.length > 1 && (
            <Fragment>
              <Button startIcon={<IconLocation fontSize="small" className={styles['icon-location']} />} onClick={openPopup}>
                <FormattedMessage
                  id={
                    multipleLocationsSelected
                      ? 'composer.labels.instagram.locations-selected'
                      : 'composer.labels.instagram.empty-location'
                  }
                />
              </Button>
              <PPDialog
                open={popupOpen}
                onClose={closePopup}
                size="lg"
                zIndex={DIALOG_Z_INDEX}
                title={<FormattedMessage id="composer.labels.select-multiple-locations" />}
              >
                <div className={styles['popup-body']}>
                  <div className={styles['albums-popup-content']}>
                    {locationEnabledProfiles.map(profile => (
                      <div className={styles['option-box']} key={profile.id}>
                        <InstagramLocationSelector
                          profile={profile}
                          selectedLocation={post.locations[profile.ppid]}
                          onSelectedLocationChange={updateSelectedLocation}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </PPDialog>
            </Fragment>
          )}
        </div>
      )}
    </div>
  )
}
